import * as Sentry from '@sentry/react';
import { SENTRY_KEY, NODE_ENV, APP_RELEASE } from './base';

// Define the mapping between environments
const environment = {
  production: 'production',
  localhost: 'development',
  staging: 'staging'
};

// Determine the Sentry environment based on NODE_ENV
const sentryEnvironment = environment[NODE_ENV] || environment.localhost;

// Configure Sentry
const sentryConfig = {
  dsn: SENTRY_KEY,
  release: APP_RELEASE,
  tunnel: '/api/v1/sentry-tunnel',
  environment: sentryEnvironment,
  autoSessionTracking: false, // we don't use Release Health
  enabled: NODE_ENV !== 'localhost', // disable sentry on localhost
  maxBreadcrumbs: 100, // 100 is the default
  normalizeDepth: 3, // 3 is the default
  denyUrls: [
    /moz-extension:\/\//, // ignore errors from browser extensions and known non-actionable sources
    /chrome-extension:\/\//,
    /safari-extension:\/\//,
    /optimizely/i,
    /hotjar/i,
    /adguard|ublock|ghostery|privacybadger/i,
    /lastpass|grammarly|gtranslate/i
  ],
  beforeSend(event) {
    // This function is called before an event is sent to Sentry
    const shouldNotSendEvent = shouldNotSendEventCheck(event);
    if (shouldNotSendEvent) {
      return null;
    }
    return event;
  }
};

// Initialize Sentry with the provided configuration
const initializeSentry = () => {
  Sentry.init(sentryConfig);
};

const sentryCaptureException = (event, context) => {
  // eslint-disable-next-line no-console
  console.error(event);
  Sentry.withScope((scope) => {
    scope.setTag('level.type', 'exception');
    scope.setLevel('error');
    // Optional structured context (e.g. failed API request details) so the error
    // can be categorized in Sentry instead of being just an opaque message.
    if (context) {
      scope.setContext('request', context);
    }
    Sentry.captureException(event);
  });
};

// Check if the event should be not send by iterating over reporting functions
const shouldNotSendEventCheck = (event) => {
  return EVENT_REPORTING_FUNCTIONS.some((func) => {
    return func(event);
  });
};

// check if the login failed because of invalid email, or missing email or password
// this is not about a wrong email or password, but about the user not entering all fields or invalid email
const handleLoginValidation = (event) => {
  const consoleEvents = event?.breadcrumbs?.filter((item) => item.category === 'console');

  if (!consoleEvents) {
    return false;
  }

  const INVALID_LOGIN_MESSAGES = [
    'E-Mail ist nicht gültig',
    'Bitte geben Sie Passwort an',
    'Bitte geben Sie E-Mail an'
  ];

  const hasInvalidLogin = consoleEvents.some((item) => {
    return INVALID_LOGIN_MESSAGES.some((message) => item.message.includes(message));
  });

  return hasInvalidLogin;
};

// Place more functions here to check for other events, they must return true if the event should not be sent
// Place the function name in the EVENT_REPORTING_FUNCTIONS array
// ...

// Array containing functions that check potential events
const EVENT_REPORTING_FUNCTIONS = [handleLoginValidation];

export { initializeSentry, sentryCaptureException };
